/**
 * O período do dashboard (H11).
 *
 * ── Por que as datas saem daqui e não do componente ───────────────────────
 *
 * Cálculo de data é onde tela erra em silêncio: fuso, mês de 31 dias, virada
 * do ano. Separado, prova-se sem navegador — e é o tipo de coisa que só quebra
 * num dia específico do ano, que nenhum teste de tela pegaria.
 *
 * ── As datas do período são do servidor ───────────────────────────────────
 *
 * A tela diz QUANTOS dias (`diasDoPeriodo`), e o servidor diz quais, no fuso
 * de `core.tenant_settings.timezone`. As datas eram calculadas aqui pelo
 * relógio UTC, e das 21h à meia-noite de Brasília o "hoje" daqui já era
 * amanhã: "7 dias" viravam 6, com um dia vazio no fim (25/09/2026).
 */

/** As opções que a tela oferece. Poucas, porque período é decisão rápida. */
export const PERIODOS = [
  { chave: '7', nome: '7 dias', dias: 7 },
  { chave: '30', nome: '30 dias', dias: 30 },
  { chave: '90', nome: '90 dias', dias: 90 },
] as const;

export type ChaveDePeriodo = (typeof PERIODOS)[number]['chave'];

export const PERIODO_PADRAO: ChaveDePeriodo = '30';

export const diasDoPeriodo = (chave: ChaveDePeriodo): number =>
  PERIODOS.find((p) => p.chave === chave)?.dias ?? 30;

/**
 * `2026-08-19` → "19/08".
 *
 * Sem o ano: o eixo de um gráfico de trinta dias não cabe com ele, e a faixa
 * inteira já está escrita por extenso no topo da tela.
 */
export function diaCurto(dia: string): string {
  const [, mes, d] = dia.split('-');
  return d && mes ? `${d}/${mes}` : dia;
}

/**
 * `2026-08-19` → "19 de agosto de 2026".
 *
 * Construída com componentes locais, e não `new Date('2026-08-19')`: a string
 * só com data é interpretada como UTC pelo padrão, e num fuso negativo ela
 * volta um dia — o título diria 18 de agosto para o dia 19.
 */
export function diaPorExtenso(dia: string): string {
  const [ano, mes, d] = dia.split('-').map(Number);
  if (!ano || !mes || !d) return dia;

  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(ano, mes - 1, d));
}

/**
 * Segundos → "3 min", "1 h 12 min", "12 s".
 *
 * Existe porque o contrato manda segundos e nenhuma tela deve mostrar "212
 * segundos": o gestor compara tempos de espera de relance, e converter de
 * cabeça é o atrito que faz ele parar de olhar.
 */
export function espera(segundos: number | null): string | null {
  if (segundos === null || !Number.isFinite(segundos)) return null;
  if (segundos < 60) return `${Math.round(segundos)} s`;

  const minutos = Math.round(segundos / 60);
  if (minutos < 60) return `${minutos} min`;

  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return resto === 0 ? `${horas} h` : `${horas} h ${resto} min`;
}

/**
 * Micros → "R$ 1,2 mi", "R$ 317 mil", "R$ 900".
 *
 * Abreviado, ao contrário do `dinheiro()` do atendimento. Lá o valor é de UMA
 * oportunidade e precisa ser exato; aqui é uma soma de carteira que aparece ao
 * lado de outras seis, e "R$ 317.100.000" em quatro linhas seguidas vira ruído
 * que ninguém compara.
 */
export function valorCurto(micros: number, moeda = 'BRL'): string {
  const reais = micros / 1_000_000;
  const formatar = (n: number, casas: number): string =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: moeda,
      minimumFractionDigits: casas,
      maximumFractionDigits: casas,
    }).format(n);

  if (Math.abs(reais) >= 1_000_000) return `${formatar(reais / 1_000_000, 1)} mi`;
  if (Math.abs(reais) >= 1_000) return `${formatar(Math.round(reais / 1_000), 0)} mil`;
  return formatar(reais, 0);
}
